/* tslint:disable:no-shadowed-variable */
import camelcaseKeysDeep from 'camelcase-keys-deep'
import { ModuleNames } from '@/store/module-names'
import { PatientNoteState } from './types'
import { ActionNames, MutationNames } from './keys'
import { getters } from './getters'
import {
  addPatientNoteQuery,
  deletePatientNoteQuery,
  getPatientNotesByPatientIdQuery,
  getPatientNotesQuery,
  updatePatientNoteQuery,
} from '@/jsonapi/PatientNotes'
import { timeout } from '@/utils/time'
import { getNoteId } from '@/utils/notes'
import { MAX_RETRY_COUNT, RETRY_INTERVAL } from '@/constants/api-config'
import { addOrUpdateSameKeyObjectInArray } from '@/store/utils/api'

const state: PatientNoteState = {
  patientNotes: [],
}

const mutations = {
  [MutationNames.updatePatientNotes](state, payload) {
    state.patientNotes = payload
  },
}

const actions = {
  async [ActionNames.getPatientNotes]({ commit }): Promise<void> {
    const results = camelcaseKeysDeep(await getPatientNotesQuery())
    commit(MutationNames.updatePatientNotes, results)
    return results
  },
  async [ActionNames.getPatientNotesByPatientId](
    { state, commit },
    { patientId }: { patientId: string }
  ): Promise<void> {
    const result = camelcaseKeysDeep(await getPatientNotesByPatientIdQuery(patientId))
    const newPatientNotes = addOrUpdateSameKeyObjectInArray(state.patientNotes, result, 'id')
    commit(MutationNames.updatePatientNotes, newPatientNotes)
  },
  async [ActionNames.addPatientNote](
    { commit, rootState, dispatch },
    payload: { patientId: string; content: string }
  ): Promise<string> {
    const { patientId, content } = payload
    const id = await addPatientNoteQuery({
      patientId,
      content,
      userId: rootState[ModuleNames.app].userId,
      userFirstName: rootState[ModuleNames.app].userFirstName,
      userLastName: rootState[ModuleNames.app].userLastName,
    })

    for (let i = 0; i < MAX_RETRY_COUNT; i++) {
      await timeout(RETRY_INTERVAL)
      const results = await dispatch(ActionNames.getPatientNotes)
      const hasAddedItem = results.some(result => {
        return getNoteId({ id: result.id, ...result.attributes }) === id
      })
      if (hasAddedItem) {
        break
      }
    }

    return id
  },
  async [ActionNames.patchPatientNote](
    { commit, rootState, dispatch },
    payload: { id: string; content: string }
  ): Promise<void> {
    await updatePatientNoteQuery({
      id: payload.id,
      content: payload.content,
    })

    for (let i = 0; i < MAX_RETRY_COUNT; i++) {
      await timeout(RETRY_INTERVAL)
      const results = await dispatch(ActionNames.getPatientNotes)
      const updatedNote = results.find(result => getNoteId({ id: result.id, ...result.attributes }) === payload.id)

      if (updatedNote && updatedNote.attributes.content === payload.content) {
        break
      }
    }
  },
  async [ActionNames.deletePatientNote]({ commit, rootState, dispatch }, payload: { id: string }): Promise<void> {
    await deletePatientNoteQuery({
      id: payload.id,
    })

    for (let i = 0; i < MAX_RETRY_COUNT; i++) {
      await timeout(RETRY_INTERVAL)
      const results = await dispatch(ActionNames.getPatientNotes)
      const deletedNote = results.find(result => getNoteId({ id: result.id, ...result.attributes }) === payload.id)

      if (!deletedNote) {
        break
      }
    }
  },
}

export default {
  name: ModuleNames.patientNotes,
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
